import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { assessmentApi } from '../services/api';
import toast from 'react-hot-toast';

const WS_URL = process.env.REACT_APP_WS_URL || `ws://${window.location.host}/ws`;

// The backend supports scoping GET /assessment/status to one specific
// entity via query params:
//   - ?assessId=<id>  — a specific historical run   (route: /assess/:id)
//   - ?projectId=<id> — a specific project's own run (route: /projects/:id)
// Passing neither returns whatever job is globally "current" (the plain
// /assess live view).
//
// Each hook instance resolves its own scope params from what it was
// actually given (historyId / projectId) or from a job id it itself just
// started — never from a shared/global variable — so two pages polling
// this endpoint at the same time never see each other's progress.
//
// A job id started via startAssessment() is also tracked locally so that,
// once a run is kicked off, this instance keeps polling that specific job
// even if it wasn't scoped by assessId/projectId up front (e.g. the plain
// /assess live view, which has neither). This local job id is mirrored
// into sessionStorage under a key namespaced by ownerKey purely so a page
// reload doesn't lose track of a job it started — still not shared across
// other pages/instances.
function jobStorageKey(ownerKey) {
  return `domo-pbi:assessment-job:${ownerKey}`;
}

function readTrackedJobId(ownerKey) {
  try {
    return window.sessionStorage.getItem(jobStorageKey(ownerKey)) || null;
  } catch (e) {
    return null;
  }
}

function writeTrackedJobId(ownerKey, jobId) {
  try {
    if (jobId) {
      window.sessionStorage.setItem(jobStorageKey(ownerKey), jobId);
    } else {
      window.sessionStorage.removeItem(jobStorageKey(ownerKey));
    }
  } catch (e) {
    // ignore storage failures (e.g. private browsing)
  }
}

/**
 * useAssessmentJob
 *
 * Manages the full assessment job lifecycle:
 *   - Polls /api/assessment/status (scoped by assessId/projectId/jobId) for
 *     job state
 *   - Connects WebSocket for real-time progress when a job is running
 *   - Loads /api/assessment/result for latest snapshot data
 *   - Provides start/cancel actions
 *
 * @param {string|number} [historyId] - when set (route: /assess/:id), loads
 *   that specific historical run's snapshot AND scopes status polling to
 *   ?assessId=<historyId>.
 * @param {string} [ownerKey] - unique id for which page/instance is using
 *   this hook (e.g. 'assess-live', `assess-history:${id}`,
 *   `project:${projectId}`). Used only to namespace this instance's own
 *   locally-tracked job id — never shared with other instances.
 * @param {object} [scope] - additional entity scope for status polling,
 *   e.g. { projectId } when used from /projects/:id. Merged with
 *   { assessId: historyId } when historyId is set.
 *
 * Returns:
 *   - status: { currentJob, lastSuccess, lastFailure }
 *   - result: latest snapshot data (discovery + pairwise summary)
 *   - progress: { stage, percent, message } (live from WebSocket, own job only)
 *   - isRunning: boolean (true only if THIS instance's own scope/job is active)
 *   - isLoading: boolean (initial data load)
 *   - startAssessment: () => Promise
 *   - cancelAssessment: () => Promise
 *   - refetchResult: () => void
 */
export function useAssessmentJob(historyId, ownerKey = 'assess-live', scope = null) {
  const queryClient = useQueryClient();
  const wsRef = useRef(null);

  // A job id THIS instance started directly (via startAssessment), tracked
  // locally so polling can follow it even when there's no assessId/
  // projectId scope up front (the plain /assess live view). Never shared
  // globally.
  const [trackedJobId, setTrackedJobId] = useState(() => readTrackedJobId(ownerKey));

  const setTrackedJob = useCallback((jobId) => {
    setTrackedJobId(jobId);
    writeTrackedJobId(ownerKey, jobId);
  }, [ownerKey]);

  // The actual params sent to /assessment/status for this instance:
  // prefer an explicit entity scope (assessId from the route, or a
  // caller-provided scope like projectId) — fall back to a directly
  // tracked job id once one has been started from this instance.
  const statusParams = {
    ...(historyId ? { assessId: historyId } : null),
    ...(scope || null),
    ...(!historyId && !scope && trackedJobId ? { assessId: trackedJobId } : null),
  };
  const hasScope = Object.keys(statusParams).length > 0;

  // Live progress state (from WebSocket)
  const [progress, setProgress] = useState({
    stage: null,
    percent: 0,
    message: null,
  });

  // ─── Poll assessment status (scoped per-instance) ───────────────────────────
  const {
    data: statusResponse,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery(
    ['assessment-status', ownerKey, historyId || null, scope ? JSON.stringify(scope) : null, trackedJobId],
    async () => {
      try {
        const resp = await assessmentApi.status(hasScope ? statusParams : undefined);
        return resp.data?.data || resp.data || null;
      } catch (err) {
        const httpStatus = err.response?.status;

        // 404 (job/entity not found or stale id) or 403 (belongs to
        // someone else) — this instance's scope/job is no longer valid.
        // Stop polling it and clear this page's own progress bar rather
        // than surfacing a hard error.
        if (hasScope && (httpStatus === 404 || httpStatus === 403)) {
          if (trackedJobId) setTrackedJob(null);
          setProgress({ stage: null, percent: 0, message: null });
          return { currentJob: null, lastSuccess: null, lastFailure: null };
        }

        throw err;
      }
    },
    {
      refetchOnWindowFocus: true,
      staleTime: 5000,
      // Poll every 3s while this instance's scoped job is running/pending,
      // otherwise fall back to a slow background refresh (for lastSuccess/
      // lastFailure info).
      refetchInterval: (data) => {
        const job = hasScope ? data?.currentJob : null;
        if (job && ['running', 'pending'].includes(job.status)) {
          return 3000;
        }
        return 30000;
      },
    }
  );

  const status = statusResponse || { currentJob: null, lastSuccess: null, lastFailure: null };

  // Only ever treat a job as "current" for this instance if it actually
  // has a scope (assessId/projectId/tracked job id) — this is what stops
  // other pages' runs from ever appearing here.
  const currentJob = hasScope ? status.currentJob : null;
  const isRunning = !!(currentJob && ['running', 'pending'].includes(currentJob.status));
  const currentJobId = currentJob?.jobId || null;

  // ─── Load latest result (snapshot) ──────────────────────────────────────────
  const {
    data: resultResponse,
    isLoading: resultLoading,
    refetch: refetchResult,
  } = useQuery(
    'assessment-result',
    async () => {
      const resp = await assessmentApi.result();
      return resp.data?.data || null;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60000,
      // When viewing a specific history entry (/assess/:id), we don't need
      // the "latest" snapshot — the history-detail query below is used
      // instead.
      enabled: !historyId,
    }
  );

  // ─── Load a specific historical assessment run (/assess/:id) ───────────────
  const {
    data: historyResponse,
    isLoading: historyLoading,
    isError: historyIsError,
    error: historyErrorObj,
  } = useQuery(
    ['assessment-history-detail', historyId],
    async () => {
      const resp = await assessmentApi.historyDetail(historyId);
      return resp.data?.data || null;
    },
    {
      enabled: !!historyId,
      refetchOnWindowFocus: false,
      staleTime: 60000,
    }
  );

  const result = historyId ? (historyResponse || null) : (resultResponse || null);

  // ─── WebSocket for live progress ────────────────────────────────────────────
  const connectWebSocket = useCallback((jobId) => {
    if (!jobId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(`${WS_URL}?jobId=${jobId}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          switch (msg.type) {
            case 'progress':
              setProgress({
                stage: msg.stage,
                percent: msg.percent,
                message: msg.message,
              });
              break;

            case 'complete':
              setProgress({ stage: 'complete', percent: 100, message: 'Assessment completed' });
              if (trackedJobId) setTrackedJob(null);
              // Refresh status and result data. Also invalidate the
              // historical-detail query (keyed by historyId) so that if
              // this job was re-run from /assess/:id, the data shown on
              // that same page updates once the run finishes, not just
              // the "latest result" used by the live /assess view.
              queryClient.invalidateQueries(['assessment-status', ownerKey]);
              queryClient.invalidateQueries('assessment-result');
              if (historyId) {
                queryClient.invalidateQueries(['assessment-history-detail', historyId]);
              }
              toast.success('Assessment completed successfully');
              break;

            case 'error':
              setProgress({ stage: 'failed', percent: 0, message: msg.error?.message || 'Assessment failed' });
              if (trackedJobId) setTrackedJob(null);
              queryClient.invalidateQueries(['assessment-status', ownerKey]);
              if (msg.error?.status === 'cancelled') {
                toast('Assessment cancelled', { icon: '⚠️' });
              } else {
                toast.error(msg.error?.message || 'Assessment failed');
              }
              break;

            default:
              break;
          }
        } catch (e) {
          console.error('[Assessment WS] Parse error:', e);
        }
      };

      ws.onerror = () => {
        console.warn('[Assessment WS] Connection error — polling fallback active');
      };

      ws.onclose = () => {
        wsRef.current = null;
      };
    } catch (e) {
      console.warn('[Assessment WS] Failed to connect:', e);
    }
  }, [queryClient, historyId, ownerKey, trackedJobId, setTrackedJob]);

  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  // Connect/disconnect WebSocket when this instance's own scoped job starts/stops
  useEffect(() => {
    if (isRunning && currentJobId) {
      connectWebSocket(currentJobId);
    } else {
      disconnectWebSocket();
    }

    return () => disconnectWebSocket();
  }, [isRunning, currentJobId, connectWebSocket, disconnectWebSocket]);

  // Sync progress from polling when WebSocket isn't connected
  useEffect(() => {
    if (isRunning && currentJob) {
      setProgress(prev => ({
        stage: currentJob.stage || prev.stage,
        percent: currentJob.percent || prev.percent,
        message: currentJob.stageMessage || prev.message,
      }));
    }
  }, [isRunning, currentJob]);

  // Reset progress when this instance's own scoped job isn't running
  useEffect(() => {
    if (!isRunning) {
      setProgress({ stage: null, percent: 0, message: null });
    }
  }, [isRunning]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const startAssessment = useCallback(async (startScope, { offline = false } = {}) => {
    try {
      // When every resource in scope came from an offline JSON upload
      // (no live Domo connection needed), hit /assessment/offline-start
      // instead of the normal /assessment/start — see
      // ProjectDetailPage.buildAssessmentScopeFromProject's isOffline
      // flag for how this is decided.
      const resp = offline
        ? await assessmentApi.startOffline(startScope)
        : await assessmentApi.start(startScope);
      const jobId = resp.data?.job?.jobId;

      if (jobId) {
        // Track this job as THIS instance's own only if it doesn't already
        // have an explicit entity scope (assessId/projectId) — those
        // already uniquely identify what to poll for. This mainly matters
        // for the plain /assess live view, which has no route/prop scope
        // of its own until a job is started.
        if (!historyId && !scope) {
          setTrackedJob(jobId);
        }
        setProgress({ stage: 'pending', percent: 0, message: 'Starting assessment...' });
        // Immediately refetch status to pick up the new job
        await refetchStatus();
        toast.success('Assessment started');
      }

      return resp.data;
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(msg);
      throw err;
    }
  }, [refetchStatus, setTrackedJob, historyId, scope]);

  const cancelAssessment = useCallback(async () => {
    try {
      await assessmentApi.cancel();
      if (trackedJobId) setTrackedJob(null);
      await refetchStatus();
      toast('Assessment cancelled', { icon: '⚠️' });
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(msg);
      throw err;
    }
  }, [refetchStatus, setTrackedJob, trackedJobId]);

  // ─── Return ─────────────────────────────────────────────────────────────────

  return {
    // State
    status: { ...status, currentJob },
    result,
    progress,
    // isRunning is true only when THIS instance's own scope (assessId,
    // projectId, or a directly-tracked job id) resolves to an
    // active/pending job — so progress bars on different pages never
    // reflect each other's jobs.
    isRunning,
    isLoading: historyId ? historyLoading : (statusLoading || resultLoading),
    isHistoryView: !!historyId,
    historyError: historyIsError ? (historyErrorObj?.response?.data?.error || historyErrorObj?.message) : null,

    // Actions
    startAssessment,
    cancelAssessment,
    refetchResult,
    refetchStatus,
  };
}
